import React, { useState } from "react";
import logo from "../../assets/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import DarkModeToggle from "../darkMode/DarkModeToggle";
import { useAuth } from "../../hooks/useAuth";
import {
  MdDashboard,
  MdPeople,
  MdPayments,
  MdAccountBalance,
  MdSettings,
  MdSecurity,
  MdMessage,
  MdLogout,
  MdMenu,
  MdClose,
  MdWallet,
  MdCurrencyBitcoin,
} from "react-icons/md";

const AdminNavbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Icons
  const DashboardIcon = MdDashboard as React.FC<React.SVGProps<SVGSVGElement>>;
  const PeopleIcon = MdPeople as React.FC<React.SVGProps<SVGSVGElement>>;
  const PaymentsIcon = MdPayments as React.FC<React.SVGProps<SVGSVGElement>>;
  const BalanceIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SettingsIcon = MdSettings as React.FC<React.SVGProps<SVGSVGElement>>;
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>;
  const MessageIcon = MdMessage as React.FC<React.SVGProps<SVGSVGElement>>;
const WalletIcon = MdWallet as React.FC<React.SVGProps<SVGSVGElement>>;
  const LogoutIcon = MdLogout as React.FC<React.SVGProps<SVGSVGElement>>;
  const MenuIcon = MdMenu as React.FC<React.SVGProps<SVGSVGElement>>;
  const CloseIcon = MdClose as React.FC<React.SVGProps<SVGSVGElement>>;
  const CryptoIcon = MdCurrencyBitcoin as React.FC<React.SVGProps<SVGSVGElement>>;

  const menuItems = [
    { path: "/admin", icon: DashboardIcon, label: "Admin Overview" },
    { path: "/admin/users", icon: PeopleIcon, label: "User Management" },
    { path: "/admin/transactions", icon: PaymentsIcon, label: "Transactions" },
    { path: "/admin/balances", icon: BalanceIcon, label: "Balance Management" },
    {
      path: "/admin/crypto-balances",
      icon: CryptoIcon,
      label: "Crypto Balances",
    },
    { path: "/admin/wallets", icon: SettingsIcon, label: "Wallet Addresses" },
    { path: "/admin/user-wallets", icon: WalletIcon, label: "User Wallets" },
    { path: "/admin/kyc", icon: SecurityIcon, label: "KYC Approvals" },
    { path: "/admin/messages", icon: MessageIcon, label: "User Messages" },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Main Navbar */}
      <nav className="w-full h-16 bg-white dark:bg-gray-900 shadow-lg fixed top-0 z-50 transition-all duration-300">
        <div className="w-full h-full px-4 lg:px-6 flex items-center justify-between">
          {/* Left Section - Logo & Menu Button */}
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isSidebarOpen ? (
                <CloseIcon className="w-6 h-6 text-purple-600" />
              ) : (
                <MenuIcon className="w-6 h-6 text-purple-600" />
              )}
            </button>

            {/* Logo */}
            <Link to="/admin" className="flex items-center gap-3">
              <img src={logo} alt="Logo" className="w-16 h-16" />
              <div className="hidden md:block">
                <h1 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  Admin Panel
                </h1>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <DarkModeToggle />

            {/* Return to User Dashboard */}
            <Link
              to="/dashboard"
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
            >
              <DashboardIcon className="w-4 h-4" />
              Dashboard
            </Link>

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Admin • Level {user?.account_level}
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm uppercase">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </span>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="border border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2"
            >
              <LogoutIcon className="w-4 h-4" />
              Logout
            </button>
          </div>

          {/* Mobile Right Section */}
          <div className="flex items-center gap-4 lg:hidden">
            <DarkModeToggle />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {isMobileMenuOpen ? (
                <CloseIcon className="w-6 h-6 text-purple-600" />
              ) : (
                <MenuIcon className="w-6 h-6 text-purple-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        <div
          className={`lg:hidden absolute top-16 left-0 w-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? "max-h-96 shadow-lg" : "max-h-0"
          }`}
        >
          <div className="flex flex-col p-4 space-y-4">
            {/* User Info */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg uppercase">
                  {user?.first_name?.[0]}
                  {user?.last_name?.[0]}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-base font-medium text-gray-800 dark:text-gray-200 capitalize">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Admin • {user?.email}
                </p>
              </div>
            </div>

            {/* Return to User Dashboard */}
            <Link
              to="/dashboard"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-purple-600 hover:bg-purple-700 text-white w-full justify-start px-4 py-3 rounded-lg transition-all duration-300 flex items-center gap-3"
            >
              <DashboardIcon className="w-5 h-5" />
              User Dashboard
            </Link>

            {/* Logout */}
            <button
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="border border-red-600 text-red-600 hover:bg-red-600 hover:text-white w-full justify-start px-4 py-3 rounded-lg transition-all duration-300 flex items-center gap-3"
            >
              <LogoutIcon className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 z-40 ${
          isSidebarOpen
            ? "w-64 translate-x-0"
            : "w-64 -translate-x-full lg:translate-x-0 lg:w-20"
        }`}
      >
        <div className="h-full py-6 px-4">
          <div className="mb-6 px-4 lg:hidden">
            <h2
              className={`font-bold text-purple-600 ${
                isSidebarOpen ? "text-lg" : "lg:text-sm lg:text-center"
              }`}
            >
              {isSidebarOpen ? "Admin Panel" : ""}
            </h2>
          </div>
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? "bg-purple-600 text-white shadow-md"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  } ${isSidebarOpen ? "justify-start" : "lg:justify-center"}`}
                >
                  <Icon
                    className={`w-6 h-6 ${isSidebarOpen ? "mr-3" : "lg:mr-0"}`}
                  />
                  <span
                    className={`font-medium transition-all duration-200 ${
                      isSidebarOpen ? "opacity-100" : "lg:opacity-0 lg:absolute"
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default AdminNavbar;
